import { SettingOutlined } from '@ant-design/icons';
import { Table, type TableProps } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { PageTitle } from '@client/components/PageTitle';
import { HideColumns } from '@client/components/table/HideColumns';
import { Toolbar } from '@client/components/Toolbar';
import { EAbilitySubjects } from '@client/hooks/useAbilities';
import { useColumnsToggle } from '@client/hooks/useColumnsToggle';
import { useEntitlements } from '@client/hooks/useEntitlements';
import { useTable } from '@client/hooks/useTable';
import { PageLayout } from '@client/layouts/page.layout';
import { columnsMetadata } from '@client/pages/entitlements/columns.metadata';
import { useLocale } from '@client/store/locale.store';
import { indexable } from '@client/utils/table/table.util';

import type { TColumns } from '@client/types/antd.type';
import type { IPricingRule } from '@common/types';

export const EntitlementsPage: React.FC = () => {
  const { t } = useTranslation();
  const { locale } = useLocale();

  const { loading, rules, total, refetch } = useEntitlements();

  const {
    tableParams: { pagination },
    filteredInfo,
    sortedInfo,
    handleTableChange,
  } = useTable<IPricingRule>(rules, total, { syncUrl: false });

  const columns: TableProps<IPricingRule>['columns'] = columnsMetadata({
    locale,
    filteredInfo,
    sortedInfo,
    entities: rules,
  });

  const { filteredColumns, columnsList, selectedColumns, setSelectedColumns } =
    useColumnsToggle<IPricingRule>(columns as TColumns<IPricingRule>[], []);

  const tableProps = {
    columns: filteredColumns as TColumns<IPricingRule>[],
    pagination,
    scroll: { x: 1000 },
    bordered: true,
    size: 'small' as const,
    dataSource: indexable<IPricingRule>(rules),
    loading,
    rowKey: (record: IPricingRule) => record.id,
    onChange: handleTableChange,
    title: () => (
      <Toolbar<IPricingRule[]>
        onRefresh={refetch}
        entities={rules}
        subject={EAbilitySubjects.ENTITLEMENTS}
      >
        <HideColumns
          columnsList={columnsList}
          selectedColumns={selectedColumns}
          onChange={setSelectedColumns}
        />
      </Toolbar>
    ),
    footer: () => <div>{t('table.total', { amount: total })}</div>,
  };

  return (
    <PageLayout
      subject={EAbilitySubjects.ENTITLEMENTS}
      title={<PageTitle Icon={SettingOutlined} name="Entitlements" />}
    >
      <Table<IPricingRule> {...tableProps} loading={loading} />
    </PageLayout>
  );
};
